
<Card class="BaseDashboardTable">
    <div class="BaseDashboardTable-header">
        <div class="BaseDashboardTable-title-row">
            <div class="BaseDashboardTable-title">
                <slot name="title"></slot>
                <span v-if="showItemCounter" class="item-counter">
                    {{ itemCount }}
                    {{ itemCount === 1 ? 'Eintrag' : 'Einträge' }}
                </span>
            </div>
            <div class="BaseDashboardTable-actions">
                <slot name="actions"></slot>
                <Popover
                    v-if="showColumnToggler"
                    class="column-toggler"
                    speed="normal"
                    horizontal-position="right"
                >
                    <template #trigger>
                        <button class="column-toggler-button">
                            <i class="ri-settings-line"></i>
                        </button>
                    </template>
                    <template #content="{ close }">
                        <div class="column-toggler-content">
                            <div class="column-toggler-header">Spalten anzeigen</div>
                            <ScrollableContainer class="column-toggler-list" max-height="300px">
                                <div
                                    v-for="column in columns"
                                    :key="column.key"
                                    class="column-toggler-item"
                                >
                                    <Checkbox
                                        :value="isColumnVisible(column)"
                                        :disabled="isColumnRequired(column)"
                                        @change="toggleColumnVisibility(column, close)"
                                    >
                                        {{ column.label }}
                                    </Checkbox>
                                </div>
                            </ScrollableContainer>
                        </div>
                    </template>
                </Popover>
                <Button
                    v-if="showExport"
                    variant="plain"
                    @onClick="handleExport('csv')"
                    size="small"
                >
                    <i class="ri-download-line" style="margin-right: 6px"></i>
                    {{ exportTitle }}
                </Button>
            </div>
        </div>
        <div
            v-if="hasSearch || (showLegend && legendItems.length > 0)"
            class="search-legend-container"
        >
            <div class="search-legend-left">
                <div v-if="hasSearch" class="search-container">
                    <i class="ri-search-line search-icon"></i>
                    <input
                        type="text"
                        class="search-input"
                        :placeholder="searchPlaceholder"
                        @input="debouncedSearch($event.target.value)"
                    />
                </div>
            </div>
            <div v-if="showLegend && legendItems.length > 0" class="BaseDashboardTable-legend">
                <TableLegend :legend-items="legendItems" />
            </div>
        </div>
    </div>
    <div
        class="BaseDashboardTable-container"
        :class="{ 'no-items': !filteredItems.length }"
        ref="tableContainer"
        :style="{ height: filteredItems.length ? containerHeight : 'auto' }"
    >
        <HoverIndicator variant="glow" ref="tableHoverIndicator" />
        <table
            :style="{ minWidth: isMobile ? 'auto' : tableMinWidth }"
            :class="{ 'hierarchical-table': useHierarchicalRows }"
        >
            <thead>
                <tr>
                    <th
                        v-if="showCounter"
                        class="counter-column text-center"
                        style="width: 50px; min-width: 50px"
                    >
                        #
                    </th>
                    <th
                        v-for="(column, index) in visibleColumns"
                        :key="index"
                        :data-priority="column.priority || 1"
                        :class="[
                            column.align ? `text-${column.align}` : '',
                            { sortable: column.sortable !== false },
                            { sorted: currentSort.column === column.key },
                            {
                                'sorted-asc':
                                    currentSort.column === column.key && currentSort.ascending,
                            },
                            {
                                'sorted-desc':
                                    currentSort.column === column.key && !currentSort.ascending,
                            },
                        ]"
                        :style="getColumnStyle(column)"
                        @click="handleHeaderClick(column)"
                    >
                        <div class="column-header">
                            {{ truncateText(column.label, headerTruncateLength) }}
                            <i
                                v-if="column.sortable !== false"
                                :class="[
                                    currentSort && currentSort.column === column.key
                                        ? currentSort.ascending
                                            ? 'ri-arrow-up-s-line active'
                                            : 'ri-arrow-down-s-line active'
                                        : 'ri-arrow-up-down-line',
                                ]"
                            ></i>
                        </div>
                    </th>

                    <th v-if="selectable" class="checkbox-column">
                        {{ selectColumnLabel }}
                        <input
                            type="checkbox"
                            :checked="allSelected"
                            @change="toggleSelectAll"
                        />
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    class="top-spacer"
                    v-if="startIndex > 0"
                    :style="{ height: topSpacerHeight + 'px' }"
                ></tr>
                <template v-for="(item, index) in visibleItems">
                    <BaseDashboardTableRow
                        :key="itemKey && item[itemKey] ? item[itemKey] : item._id || index"
                        :item="item"
                        :index="startIndex + index + 1"
                        :data-index="startIndex + index"
                        :show-counter="showCounter"
                        :columns="visibleColumns"
                        :is-clickable="isClickable"
                        :selectable="selectable"
                        :has-any-expandable-rows="hasAnyExpandableRows"
                        :is-checked="isChecked(item)"
                        :expanded="isRowExpanded(item)"
                        :is-selected="isSelected(item)"
                        :cell-truncate-length="cellTruncateLength"
                        :global-components="globalComponents"
                        :search-query="searchQuery"
                        :searchable-columns="searchableColumns"
                        :is-disabled="item.isDisabled"
                        :border-color="getRowBorderColor(item)"
                        @row-click="
                            handleRowClick($event.item, startIndex + $event.index, $event.event)
                        "
                        @row-hover="handleRowHover($event.item, startIndex + $event.index)"
                        @row-leave="handleRowLeave($event.item, startIndex + $event.index)"
                        @toggle-expand="toggleExpand($event.item, startIndex + $event.index)"
                        @item-checked="toggleItemChecked"
                    >
                        <template
                            v-for="column in visibleColumns"
                            #[`column-${column.key}`]="slotProps"
                        >
                            <slot
                                :name="`column-${column.key}`"
                                v-bind="{ ...slotProps, item, index: startIndex + index }"
                            ></slot>
                        </template>
                    </BaseDashboardTableRow>

                    <tr
                        v-if="!useHierarchicalRows && isRowExpanded(item)"
                        :key="`${startIndex + index}-expanded`"
                        class="nested-table-row"
                    >
                        <td :colspan="visibleColumns.length">
                            <div class="nested-table-container">
                                <slot
                                    name="nested-table"
                                    :item="item"
                                    :index="startIndex + index"
                                ></slot>
                            </div>
                        </td>
                    </tr>
                </template>

                <tr
                    class="bottom-spacer"
                    v-if="endIndex < filteredItems.length"
                    :style="{ height: bottomSpacerHeight + 'px' }"
                ></tr>
            </tbody>
        </table>
    </div>

    <div v-if="!filteredItems.length" class="table-empty">
        <i class="ri-inbox-line"></i>
        <p>{{ searchQuery ? 'Keine Ergebnisse gefunden.' : emptyMessage }}</p>
    </div>
</Card>
